import {
  Carousel,
  CarouselSlide,
} from '@monofront/design-system/components/Carousel';
import { useMediaQuery } from '@monofront/business/hooks';
import { ICardProductInfos } from '@monofront/business/hooks';
import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  ISearchProduct,
  PrismicLinkContext,
} from '@monofront/types';
import { ICarouselProduct, ICarouselCard } from '@monofront/types';
import CarouselProductCard from './CarouselProductCard';
import EditorialCard from '@monofront/design-system/components/EditorialCard';
import { KeyTextField } from '@prismicio/client';

import type { JSX } from "react";

interface ICarouselProductsProps {
  headerTitle?: JSX.Element;
  products: ISearchProduct[] | ICarouselProduct[];
  isPurchasable?: boolean;
  containerContext?: AddToShoppingCartContext;
  trackingId?: string | KeyTextField;
  card?: ICarouselCard;
  onCardClick?: (product: Partial<ICardProductInfos>, index: number) => void;
  isOnCardClickDisabled?: boolean;
  isProductDrawerEnabled?: boolean;
  trackProductInteraction?: (
    product: Partial<ICardProductInfos>,
    index: number,
  ) => void;
}

const CarouselProducts: React.FC<ICarouselProductsProps> = ({
  headerTitle = null,
  products,
  isPurchasable = true,
  containerContext = { source: AddToShoppingCartSource.CAROUSEL },
  trackingId,
  card,
  onCardClick,
  isOnCardClickDisabled,
  isProductDrawerEnabled = false,
  trackProductInteraction,
}) => {
  const isMobile = useMediaQuery(800);
  const carouselProducts = products.filter(
    (product) =>
      product.image ||
      (Array.isArray(product.images) && product.images.length > 0),
  );

  const Card = card && (
    <EditorialCard
      title={
        <p className="accent-title">
          <strong>{card.title}</strong>
        </p>
      }
      content={<p>{card.description}</p>}
      illustration={card.illustration}
      backgroundColor={card.backgroundColor}
      ctaLink={
        card.link && card.label
          ? {
              link: card.link,
              label: card.label,
              trackingId: card.trackingId,
              context: PrismicLinkContext.SHOP,
            }
          : null
      }
      ctaButton={
        card.label && card.onClick
          ? {
              label: card.label,
              onClick: card.onClick,
            }
          : null
      }
    />
  );

  return (
    <div className="container">
      <Carousel
        headerTitle={
          headerTitle ? <div className="title">{headerTitle}</div> : null
        }
        card={!isMobile && Card ? Card : undefined}
        hasGradient
        hasCustomNagivation
        slidesPerView="auto"
        navigation
        mousewheel
        cssMode
      >
        {isMobile && Card && <CarouselSlide>{Card}</CarouselSlide>}
        {carouselProducts.map((product, i) => {
          return (
            <CarouselSlide key={`${product.id}-${i}`} virtualIndex={2}>
              <div className="tw-mx-1">
                <CarouselProductCard
                  product={product}
                  containerContext={containerContext}
                  isPurchasable={isPurchasable}
                  trackingId={trackingId}
                  onCardClick={(product) => onCardClick?.(product, i)}
                  isOnCardClickDisabled={isOnCardClickDisabled}
                  isProductDrawerEnabled={isProductDrawerEnabled}
                  trackProductInteraction={() =>
                    trackProductInteraction?.(product, i)
                  }
                />
              </div>
            </CarouselSlide>
          );
        })}
      </Carousel>
      <style jsx>{`
        .container :global(.swiper-slide > div:first-child) {
          height: 100%;
          border-radius: 0;
        }

        .container :global(.swiper-container) {
          border-radius: var(--rd-4);
        }

        .title {
          margin-bottom: var(--space-4);
        }
      `}</style>
    </div>
  );
};

export default CarouselProducts;
